// src/contexts/AuthContext.js
import { onIdTokenChanged } from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { auth } from "../firebase";
import { clearLoginPasswordRecovery } from "../services/loginPasswordRecovery";
import {
  addCrashBreadcrumb,
  setUserContext as setCrashUserContext,
} from "../services/clientCrashReporter";
import {
  getProductRoleFromClaims,
  summarizeProductClaims,
} from "../services/authClaims";
import { resolveDeferredFunnelEventIdentity } from "../services/funnel/funnelEventStore";
import {
  getCanonicalAuthUserId,
  isCanonicalAuthUser,
} from "../services/authIdentity";

const CLAIMS_NETWORK_RETRY_DELAYS_MS = [500, 1500];
const CLAIMS_NETWORK_RECOVERY_DELAY_MS = 10_000;
const isAuthNetworkError = (error) =>
  error?.code === "auth/network-request-failed";

const getAuthFailureDetails = (operation, error) => ({
  operation,
  reasonCode: /^auth\/[a-z0-9-]{1,64}$/.test(error?.code || "")
    ? error.code.replace(/[/-]/g, "_")
    : "auth_unknown",
});

export const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [customClaims, setCustomClaims] = useState(null); // State for custom claims
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authFailureDetails, setAuthFailureDetails] = useState(null);
  const authEventRef = useRef(0);
  const networkRecoveryAttemptedForRef = useRef(null);

  const storeReferralInLocalStorage = () => {
    const query = new URLSearchParams(window.location.search);
    // Legacy referral program
    const referralParam = query.get("ref");

    if (referralParam) {
      try {
        localStorage.setItem("referrerId", referralParam);
      } catch {
        addCrashBreadcrumb({
          category: "auth",
          message: "referral_storage_failed",
        });
      }
    }
  };

  const loadAuthUser = useCallback(
    async (user, { forceRefresh = false } = {}) => {
      const authEvent = authEventRef.current + 1;
      authEventRef.current = authEvent;
      setAuthError(null);
      setAuthFailureDetails(null);
      setLoading(true);
      let claimsAttemptCount = 0;

      try {
        if (user) {
          let idToken;
          for (let attempt = 0; attempt <= CLAIMS_NETWORK_RETRY_DELAYS_MS.length; attempt += 1) {
            try {
              claimsAttemptCount += 1;
              idToken = await user.getIdTokenResult(forceRefresh);
              break;
            } catch (error) {
              if (!isAuthNetworkError(error) || attempt === CLAIMS_NETWORK_RETRY_DELAYS_MS.length) {
                throw error;
              }
              await new Promise((resolve) => {
                window.setTimeout(resolve, CLAIMS_NETWORK_RETRY_DELAYS_MS[attempt]);
              });
              if (authEvent !== authEventRef.current || auth.currentUser !== user) return null;
            }
          }
          if (auth.currentUser !== user) return null;
          if (authEvent !== authEventRef.current) {
            // Firebase's token-change listener can supersede a successful forced
            // refresh. It owns state updates, but the same-user caller still
            // needs the verified claims returned by its refresh.
            return forceRefresh ? idToken.claims : null;
          }
          setCurrentUser(user);
          setCustomClaims(idToken.claims);
          networkRecoveryAttemptedForRef.current = null;
          return idToken.claims;
        }

        setCurrentUser(null);
        setCustomClaims(null);
        clearLoginPasswordRecovery();
        return null;
      } catch (error) {
        if (authEvent !== authEventRef.current || (user && auth.currentUser !== user)) {
          return null;
        }
        console.error("Failed to load auth claims:", error);
        setAuthError(error);
        setAuthFailureDetails({
          ...getAuthFailureDetails("auth_claims_load", error),
          attemptCount: claimsAttemptCount,
          transportPhase: "firebase_id_token",
        });
        setCurrentUser(user || null);
        setCustomClaims(null);
        addCrashBreadcrumb({
          category: "auth",
          message: "claims_load_failed",
          data: {
            code: error?.code || error?.name || "unknown",
          },
        });
        return null;
      } finally {
        if (authEvent === authEventRef.current) {
          setLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    storeReferralInLocalStorage();

    const unsubscribe = onIdTokenChanged(
      auth,
      (user) => {
        void loadAuthUser(user);
      },
      (error) => {
        authEventRef.current += 1;
        console.error("Auth state listener failed:", error);
        setAuthError(error);
        setAuthFailureDetails(getAuthFailureDetails("auth_state_listener", error));
        setCurrentUser(null);
        setCustomClaims(null);
        setLoading(false);
        addCrashBreadcrumb({
          category: "auth",
          message: "auth_state_failed",
          data: {
            code: error?.code || error?.name || "unknown",
          },
        });
      },
    );

    return () => {
      authEventRef.current += 1;
      unsubscribe();
    };
  }, [loadAuthUser]);

  useEffect(() => {
    if (!isAuthNetworkError(authError) || !currentUser) return undefined;
    const failedAuthEvent = authEventRef.current;
    const recoverOnline = () => {
      if (
        failedAuthEvent === authEventRef.current &&
        auth.currentUser === currentUser
      ) {
        void loadAuthUser(currentUser);
      }
    };
    // Browsers can remain "online" while Firebase Auth briefly cannot reach
    // its token endpoint. Give that exact failed user one later recovery attempt;
    // a persistent failure stays terminal and is not polled indefinitely.
    let recoveryTimer = null;
    if (networkRecoveryAttemptedForRef.current !== currentUser.uid) {
      networkRecoveryAttemptedForRef.current = currentUser.uid;
      recoveryTimer = window.setTimeout(recoverOnline, CLAIMS_NETWORK_RECOVERY_DELAY_MS);
    }
    window.addEventListener("online", recoverOnline);
    return () => {
      if (recoveryTimer !== null) window.clearTimeout(recoveryTimer);
      window.removeEventListener("online", recoverOnline);
    };
  }, [authError, currentUser, loadAuthUser]);

  const refreshClaims = useCallback(
    async (userOverride = null) => {
      const targetUser = userOverride || auth.currentUser || currentUser;
      return loadAuthUser(targetUser || null, { forceRefresh: true });
    },
    [currentUser, loadAuthUser],
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    const canonicalUserId = getCanonicalAuthUserId(currentUser);
    resolveDeferredFunnelEventIdentity({
      userId: canonicalUserId || null,
      memberId: summarizeProductClaims(customClaims)?.isMember ? canonicalUserId || null : null,
    });
    const claimsSummary = summarizeProductClaims(customClaims);
    const role = getProductRoleFromClaims(customClaims);

    setCrashUserContext({
      uid: canonicalUserId || null,
      role,
      isAuthenticated: isCanonicalAuthUser(currentUser),
      claimsSummary,
    });

    addCrashBreadcrumb({
      category: "auth",
      message: isCanonicalAuthUser(currentUser)
        ? "authenticated"
        : currentUser?.isAnonymous
          ? "anonymous_guest"
          : "signed_out",
      data: {
        role,
        hasClaims: Boolean(claimsSummary),
        isAdmin: Boolean(claimsSummary?.isAdmin),
        isHelper: Boolean(claimsSummary?.isHelper),
        isMember: Boolean(claimsSummary?.isMember),
      },
    });
  }, [loading, currentUser?.uid, currentUser?.isAnonymous, customClaims]);

  const getIdToken = useCallback(async () => {
    if (!currentUser) return null;
    return currentUser.getIdToken();
  }, [currentUser]);

  const value = useMemo(
    () => ({
      currentUser,
      customClaims,
      getIdToken,
      refreshClaims,
      loading,
      authError,
      authFailureDetails,
    }),
    [
      authError,
      authFailureDetails,
      currentUser,
      customClaims,
      getIdToken,
      loading,
      refreshClaims,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
